import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  WORKSPACE_NAME_MAX_LENGTH,
  WORKSPACE_NAME_MIN_LENGTH,
  WORKSPACE_NAME_PROBLEM_MESSAGES,
  findWorkspaceNameProblem,
  normalizeWorkspaceName,
} from '@terafile/shared'
import { type FormEvent, useEffect, useState } from 'react'
import { SUBMIT_ERROR_FALLBACK, toUserMessage } from '../../lib/error-copy.js'
import { LOADING } from '../../lib/labels.js'
import { updateMeTenant } from '../../lib/me-api.js'
import type { MeTenant } from '../../lib/me-api.js'
import { useToast } from '../../lib/toast.js'
import { SettingsSectionCard, SettingsUnavailableNotice } from './SettingsSectionCard.js'

type Props = {
  tenant: MeTenant
}

const TITLE = 'ワークスペース名'
const DESCRIPTION =
  '管理画面・各種通知メールでこの名前が使われます。ほかのワークスペースと同じ名前は使えません。'

export function SettingsWorkspaceSection({ tenant }: Props) {
  return (
    <SettingsSectionCard title={TITLE} description={DESCRIPTION}>
      {tenant.kind === 'EPHEMERAL' ? (
        <SettingsUnavailableNotice message="お試し中は名前を変えられません。アカウント登録すると編集できるようになります。" />
      ) : (
        <WorkspaceNameForm current={tenant.name ?? ''} />
      )}
    </SettingsSectionCard>
  )
}

function WorkspaceNameForm({ current }: { current: string }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [value, setValue] = useState(current)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setValue(current)
  }, [current])

  const mutation = useMutation({
    mutationFn: (name: string) => updateMeTenant({ name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me-tenant'] })
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
      toast.success('ワークスペース名を変更しました')
    },
    onError: (err) => setError(toUserMessage(err, SUBMIT_ERROR_FALLBACK)),
  })

  // API と同じく、全角・大文字・前後の空白をそろえてから比べる
  const normalized = normalizeWorkspaceName(value)

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (normalized === current) return
    const problem = findWorkspaceNameProblem(normalized)
    if (problem) {
      setError(WORKSPACE_NAME_PROBLEM_MESSAGES[problem])
      return
    }
    mutation.mutate(normalized)
  }

  return (
    <WorkspaceNameFormView
      value={value}
      onChange={setValue}
      onSubmit={onSubmit}
      error={error}
      isPending={mutation.isPending}
      isUnchanged={normalized === current}
    />
  )
}

type FormViewProps = {
  value: string
  onChange: (value: string) => void
  onSubmit: (e: FormEvent) => void
  error: string | null
  isPending: boolean
  isUnchanged: boolean
}

function WorkspaceNameFormView({
  value,
  onChange,
  onSubmit,
  error,
  isPending,
  isUnchanged,
}: FormViewProps) {
  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <input
        type="text"
        aria-label={TITLE}
        autoCapitalize="none"
        spellCheck={false}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={WORKSPACE_NAME_MAX_LENGTH}
        aria-invalid={error !== null}
        aria-describedby="settings-workspace-name-hint"
        className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background"
        placeholder="例: sample-studio"
      />
      <p id="settings-workspace-name-hint" className="text-xs text-muted-foreground">
        半角の英小文字・数字・ハイフン（-）で{WORKSPACE_NAME_MIN_LENGTH}〜
        {WORKSPACE_NAME_MAX_LENGTH}
        文字。大文字や全角で入力しても、半角の小文字にそろえて保存します。
      </p>
      {error && (
        <p
          role="alert"
          className="text-xs text-destructive bg-destructive/5 border border-destructive/30 rounded-md px-3 py-2"
        >
          {error}
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        <button
          type="submit"
          disabled={isPending || isUnchanged}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
        >
          {isPending ? LOADING.saving : '保存'}
        </button>
      </div>
    </form>
  )
}
